/**
 * deck.gl 캔버스 — **표시만 한다** (DESIGN §8.2 · §8.2.1 · §8.2.2).
 *
 * **베이스맵은 설정이 있을 때만 불러온다.** 뷰포트가 곧 "어느 지역을 조사하는가"이고
 * 그것이 매번 제3자 타일 서버 로그에 남으므로 기본값이 될 수 없다 (§8.2.2).
 * `settings.toml` 의 `[map] basemap_style_url` 에 사람이 명시적으로 URL 을 넣으면
 * 그때만 요청한다. 비어 있으면 예전처럼 양피지 배경 + 우리 레이어만 그린다.
 *
 * 데이터·색 구간은 페이지가 준다 — 캔버스가 따로 가져오면 지도의 색과 표의 숫자가
 * 어긋나는 순간이 생긴다.
 *
 * **3D 높이·마크 반경으로 값을 인코딩하지 않는다** (§8.2.1 규칙 1). 원기둥은 시점에
 * 따라 서로를 가려 비교가 불가능해진다.
 */

import { H3HexagonLayer, TileLayer } from '@deck.gl/geo-layers'
import { BitmapLayer, ScatterplotLayer } from '@deck.gl/layers'
import DeckGL from '@deck.gl/react'
import { useMemo } from 'react'

import { rgb, type Bins } from '@/lib/bins'
import { cssVar } from '@/lib/cssvar'
import { int, pct } from '@/lib/fmt'
import type { Cell, ViewState } from './useGeoData'

export type BasemapMode = 'full' | 'muted' | 'none'

/**
 * 채도를 빼고 흰색 쪽으로 민 배경 타일.
 *
 * CSS `filter: grayscale()` 을 쓸 수 없다 — deck.gl 은 배경과 데이터를 **한
 * 캔버스**에 그리므로 캔버스에 필터를 걸면 우리 데이터까지 회색이 된다.
 * 그래서 배경 레이어의 프래그먼트 셰이더에만 손댄다.
 */
class MutedBitmapLayer extends BitmapLayer {
  static layerName = 'MutedBitmapLayer'

  getShaders() {
    const shaders = super.getShaders()
    return {
      ...shaders,
      inject: {
        ...(shaders.inject ?? {}),
        // Rec.601 휘도로 회색을 만든 뒤 흰색과 섞어 연하게 만든다
        'fs:DECKGL_FILTER_COLOR': `
          float lum = dot(color.rgb, vec3(0.299, 0.587, 0.114));
          color.rgb = mix(vec3(lum), vec3(1.0), 0.45);
        `,
      },
    }
  }
}

interface Props {
  cells: Cell[]
  bins: Bins
  isPoints: boolean
  view: ViewState
  onViewChange: (next: ViewState) => void
  height: number
  /** 래스터 타일 URL 템플릿. 비어 있으면 **요청하지 않는다** (§8.2.2) */
  basemapUrl?: string
  /** 배경 표현. 'none' 이면 타일을 아예 요청하지 않는다 */
  basemapMode?: BasemapMode
}

export function MapCanvas({
  cells, bins, isPoints, view, onViewChange, height, basemapUrl,
  basemapMode = 'full',
}: Props) {
  const missing = cssVar('--c-na', '#6f6f75')

  /**
   * 배경 타일. **URL 이 없으면 레이어 자체를 만들지 않는다** — 빈 문자열로
   * TileLayer 를 만들면 잘못된 주소로 요청이 나간다.
   * 우리 데이터 아래에 깔리도록 layers 배열 맨 앞에 둔다.
   */
  const base = useMemo(() => {
    // **'none' 이면 레이어를 만들지 않는다** — 만들면 요청이 나간다.
    // 이것이 §8.2.2 의 통제권을 사용자에게 돌려주는 지점이다.
    if (!basemapUrl || basemapMode === 'none') return []
    const Bmp = basemapMode === 'muted' ? MutedBitmapLayer : BitmapLayer
    return [
      new TileLayer<ImageBitmap>({
        id: `basemap-${basemapMode}`,
        data: basemapUrl,
        minZoom: 0,
        maxZoom: 19,
        tileSize: 256,
        // 배경이 데이터보다 튀면 안 된다 — 눌러서 우리 색이 위에 서게 한다
        opacity: basemapMode === 'muted' ? 0.85 : 0.55,
        renderSubLayers: (props) => {
          const bb = props.tile.boundingBox as [number[], number[]]
          const [w, s] = bb[0]
          const [e, n] = bb[1]
          if (w == null || s == null || e == null || n == null) return null
          return new Bmp({
            id: `${props.id}-bmp`,
            image: props.data,
            bounds: [w, s, e, n],
          })
        },
      }),
    ]
  }, [basemapUrl, basemapMode])

  const layers = useMemo(() => {
    const fill = (c: Cell): [number, number, number, number] => {
      const i = bins.indexOf(c.value)
      // 값 없음은 색 계열에서 빼고 회색 반투명으로 둔다 — 0 과 같은 색을 주면
      // "관측 안 됨"이 "값이 0"으로 읽힌다
      if (i == null) return [...rgb(missing), 60]
      return [...rgb(bins.colors[i] ?? missing), 205]
    }

    if (isPoints) {
      return [
        new ScatterplotLayer<Cell>({
          id: 'chargers',
          data: cells,
          getPosition: (d) => [d.lng, d.lat],
          getFillColor: fill,
          getRadius: 40,
          radiusMinPixels: 2.5,
          radiusMaxPixels: 5,
          stroked: false,
          pickable: true,
          updateTriggers: { getFillColor: [bins] },
        }),
      ]
    }
    return [
      new H3HexagonLayer<Cell>({
        id: 'hex',
        data: cells,
        getHexagon: (d) => d.id,
        getFillColor: fill,
        extruded: false,
        stroked: true,
        getLineColor: [255, 255, 255, 80],
        lineWidthMinPixels: 0.5,
        pickable: true,
        updateTriggers: { getFillColor: [bins] },
      }),
    ]
  }, [cells, bins, isPoints, missing])

  return (
    <div
      className={`mcanvas${basemapUrl ? ' mcanvas--base' : ''}`}
      style={{ height }}
      data-testid="map-canvas"
    >
      <DeckGL
        viewState={view}
        onViewStateChange={(e) => onViewChange(e.viewState as ViewState)}
        controller={{ dragRotate: false, touchRotate: false }}
        layers={[...base, ...layers]}
        getTooltip={({ object }) => {
          const c = object as Cell | null
          if (!c) return null
          const label = isPoints ? c.id : `${c.id.slice(0, 8)}…`
          return {
            html:
              `<b>${label}</b><br/>충전기 ${int(c.n_chargers)}기` +
              `<br/>가동률 ${pct(c.availability)}<br/>커버리지 ${pct(c.coverage, 1)}`,
            style: {
              fontSize: '11px',
              padding: '6px 8px',
              background: '#1d1d1f',
              color: '#f5f5f7',
              borderRadius: '8px',
            },
          }
        }}
      />
    </div>
  )
}
